import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAtom } from "jotai";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  FaEye,
  FaEdit,
  FaPlus,
  FaSearch,
  FaHistory,
  FaExchangeAlt,
  FaWarehouse,
  FaBoxOpen,
  FaChevronLeft,
  FaChevronRight,
  FaFilter,
  FaDownload,
  FaCalendarAlt,
  FaTag,
  FaList,
  FaArrowLeft,
} from "react-icons/fa";
import axios from "axios";
import toast from "react-hot-toast";
import moment from "moment";
import { DatePicker, Select, Input, Space, Button as AntButton, Tag, Tooltip as AntTooltip, Spin } from "antd";
import { gbState, fYear } from "../../../components/shared/GlobalState";

const AssetInventoryChild = () => {
  let navigate = useNavigate();
  const [data, setData] = useState([]);
  const [financial_year] = useAtom(fYear);
  const [{ errorLogApi, user }] = useAtom(gbState);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [shouldDisplayButton, setShouldDisplayButton] = useState(false);
  
  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [assetNameFilter, setAssetNameFilter] = useState("");
  const [closingStockFilter, setClosingStockFilter] = useState(null);
  const [stockRangeFilter, setStockRangeFilter] = useState([null, null]);
  const [exportLoading, setExportLoading] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();
    const dayOfMonth = today.getDate();

    // Get the last day of the current month
    const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0).getDate();

    // Check if today is one of the last two days of the month
    if (dayOfMonth === lastDayOfMonth || dayOfMonth === lastDayOfMonth - 1) {
      setShouldDisplayButton(true);
    } else {
      setShouldDisplayButton(false);
    }
  }, []);

  useEffect(() => {
    getDataFromServer();
  }, [financial_year]);

  const getDataFromServer = () => {
    let userToken = localStorage.getItem("token");

    let urlLink = `${process.env.REACT_APP_API_URL}/church/stock/${user.church_id}`;

    const config = {
      method: "GET",
      url: urlLink,
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
    };
    axios(config)
      .then(function (response) {
        //console.log(JSON.stringify(response.data.users));
        // const oldData = response.data.current_stock;
        const oldData = response.data.current_stock?.filter(
          (finFilter) =>
            finFilter?.current_stock?.financial_year === financial_year?.label
        );
        const updatedData = oldData.map((data) => {
          return {
            //   ...data,
            id: data?._id,
            asset_id: data?.current_stock?.asset_id?._id,
            item_code: data?.current_stock?.asset_id?.item_code,
            asset_name: data?.current_stock?.asset_id?.asset_name,
            opening_stock: data?.current_stock?.opening_stock,
            inward_qty: data?.current_stock?.inward_qty,
            outward_qty: data?.current_stock?.outward_qty,
            closing_stock: data?.current_stock?.closing_stock,
          };
        });

        setData(updatedData);
      })
      .catch(function (err) {
        console.log("error api call ", err);
      });
  };

  const filteredData = useMemo(() => {
    return data.filter((item) => {
      // Search term filter
      const matchesSearch = Object.values(item).some((value) => {
        // Check if value is null or undefined before calling toString()
        if (value === null || value === undefined) return false;
        return value.toString().toLowerCase().includes(searchTerm.toLowerCase());
      });
      
      // Asset name filter
      let matchesAssetName = true;
      if (assetNameFilter && item.asset_name) {
        matchesAssetName = item.asset_name.toLowerCase().includes(assetNameFilter.toLowerCase());
      }
      
      // Closing stock filter
      let matchesClosingStock = true;
      if (closingStockFilter !== null && item.closing_stock !== undefined) {
        if (closingStockFilter === 'zero') {
          matchesClosingStock = parseInt(item.closing_stock) === 0;
        } else if (closingStockFilter === 'low') {
          matchesClosingStock = parseInt(item.closing_stock) > 0 && parseInt(item.closing_stock) <= 5;
        } else if (closingStockFilter === 'normal') {
          matchesClosingStock = parseInt(item.closing_stock) > 5;
        }
      }
      
      // Stock range filter
      let matchesStockRange = true;
      if (stockRangeFilter[0] !== null && stockRangeFilter[1] !== null && item.closing_stock !== undefined) {
        const stockValue = parseInt(item.closing_stock);
        const minStock = parseInt(stockRangeFilter[0]);
        const maxStock = parseInt(stockRangeFilter[1]);
        matchesStockRange = stockValue >= minStock && stockValue <= maxStock;
      }
      
      return matchesSearch && matchesAssetName && matchesClosingStock && matchesStockRange;
    });
  }, [data, searchTerm, assetNameFilter, closingStockFilter, stockRangeFilter]);

  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);
  
  // Clear all filters
  const clearFilters = () => {
    setAssetNameFilter('');
    setClosingStockFilter(null);
    setStockRangeFilter([null, null]);
  };
  
  // Export functionality
  const handleExport = async () => {
    try {
      setExportLoading(true);
      
      // Create CSV content
      const headers = ['Item Code', 'Asset Name', 'Opening Stock', 'Inward QTY', 'Outward QTY', 'Closing Stock'];
      
      let csvContent = headers.join(',') + '\n';
      
      // Use filtered data for export
      filteredData.forEach(row => {
        const rowData = [
          `"${row.item_code || ''}"`,
          `"${row.asset_name || ''}"`,
          `"${row.opening_stock || '0'}"`,
          `"${row.inward_qty || '0'}"`,
          `"${row.outward_qty || '0'}"`,
          `"${row.closing_stock || '0'}"`,
        ];
        csvContent += rowData.join(',') + '\n';
      });
      
      // Create download link
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `asset-inventory-${moment().format('YYYY-MM-DD')}.csv`);
      document.body.appendChild(link);
      
      // Trigger download
      link.click();
      document.body.removeChild(link);
      
      toast.success('Export completed successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export data');
    } finally {
      setExportLoading(false);
    }
  };

  const handleAssetStockSettlement = () => {
    let userToken = localStorage.getItem("token");
    const config = {
      method: "post",
      url: `${process.env.REACT_APP_API_URL}/church/stocksettle/${user.church_id}`,
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
    };
    axios(config)
      .then(function (response) {
        getDataFromServer();
        toast.success("Asset Settled Successfully.");
      })
      .catch(function (error) {
        //  toast.error(JSON.stringify(error.response.data.message));
        toast.error(error?.response?.data?.error);
        errorLogApi(error, "AssetStockSettleMent", "/church/stocksettle/");
        // console.log(error);
      });
  };

  const renderTableHeader = () => (
    <TableHead className="bg-gray-100">
      <TableRow>
        <TableCell className="font-semibold text-gray-700">Action</TableCell>
        <TableCell className="font-semibold text-gray-700">Asset Code / Name</TableCell>
        {!isMobile && (
          <>
            <TableCell align="center" className="font-semibold text-gray-700">Opening Stock</TableCell>
            <TableCell align="center" className="font-semibold text-gray-700">Inward QTY</TableCell>
            <TableCell align="center" className="font-semibold text-gray-700">Outward QTY</TableCell>
          </>
        )}
        <TableCell align="center" className="font-semibold text-gray-700">Closing Stock</TableCell>
      </TableRow>
    </TableHead>
  );

  const renderTableRow = (row) => (
    <TableRow 
      key={row.id}
      className="transition-colors duration-150 hover:bg-gray-50"
    >
      <TableCell>
        <div className="flex items-center space-x-2">
          <Tooltip title="Issue Asset">
            <IconButton
              size="small"
              className="p-1 bg-blue-50 transition-colors duration-150 hover:bg-blue-100"
              onClick={() =>
                navigate("/assets/inventory/issue", { state: row })
              }
            >
              <FaBoxOpen className="text-blue-600" />
            </IconButton>
          </Tooltip>
          {/* <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => navigate("/assets/inventory/edit", { state: row })}
            >
              <FaEdit className="text-gray-600" />
            </IconButton>
          </Tooltip> */}
        </div>
      </TableCell>
      <TableCell>
        <div className="flex flex-col">
          <span className="font-medium text-gray-800">{row.asset_name}</span>
          <span className="text-xs text-gray-500">{row.item_code}</span>
        </div>
      </TableCell>
      {!isMobile && (
        <>
          <TableCell align="center" className="font-medium">{row.opening_stock}</TableCell>
          <TableCell align="center" className="font-medium text-green-600">{row.inward_qty}</TableCell>
          <TableCell align="center" className="font-medium text-red-600">{row.outward_qty}</TableCell>
        </>
      )}
      <TableCell align="center" className="font-semibold text-gray-800">{row.closing_stock}</TableCell>
    </TableRow>
  );

  return (
    <div className="p-3 w-full">
      <div className="overflow-visible mb-6 bg-white rounded-xl shadow-md">
        <div className="p-4">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center">
              <div className="flex justify-center items-center mr-4 w-10 h-10 bg-blue-800 rounded-lg">
                <FaWarehouse className="text-white" size={20} />
              </div>
              <h1 className="text-xl font-semibold text-gray-800">
                Asset Inventory
              </h1>
            </div>
            <div className="flex flex-wrap gap-2">
              {shouldDisplayButton && (
                <button
                  className="flex items-center px-3 py-2 text-amber-700 bg-amber-50 rounded-lg border border-amber-200 transition-colors hover:bg-amber-100"
                  onClick={handleAssetStockSettlement}
                >
                  <FaExchangeAlt className="mr-2" /> {!isMobile && "Settle Stock"}
                </button>
              )}
            </div>
          </div>
          <div className="mb-5 border-b border-gray-200"></div>
          
          <div className="grid grid-cols-1 gap-4 justify-end mb-5 md:grid-cols-2 lg:grid-cols-4">
            {/* <button
              className="flex justify-center items-center p-3 text-indigo-700 bg-indigo-50 rounded-lg border border-indigo-200 transition-colors hover:bg-indigo-100"
              onClick={() => navigate("/assets/settlement/history")}
            >
              <FaHistory className="mr-2" /> Settlement History
            </button> */}
            <button
              className="flex justify-center items-center p-3 text-purple-700 bg-purple-50 rounded-lg border border-purple-200 transition-colors hover:bg-purple-100"
              onClick={() => navigate("/assets/inventory/issue/history")}
            >
              <FaBoxOpen className="mr-2" /> Outward History
            </button>
            <button
              className="flex justify-center items-center p-3 text-cyan-700 bg-cyan-50 rounded-lg border border-cyan-200 transition-colors hover:bg-cyan-100"
              onClick={() => navigate("/assets/inventory/inward/history")}
            >
              <FaWarehouse className="mr-2" /> Inward History
            </button>
            <button
              className="flex justify-center items-center p-3 text-green-700 bg-green-50 rounded-lg border border-green-200 transition-colors hover:bg-green-100"
              onClick={() => navigate("/assets/inventory/add")}
            >
              <FaPlus className="mr-2" /> Asset Inward
            </button>
          </div>
          
          <div className="flex gap-2 mb-4">
            <button 
              className={`px-4 py-2 rounded-lg border flex items-center ${showFilters ? 'text-blue-800 bg-blue-50 border-blue-800' : 'text-blue-800 border-blue-800 hover:bg-blue-50'}`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <FaFilter className="mr-2" /> {showFilters ? 'Hide Filters' : 'Show Filters'}
            </button>
            <button
              className="flex items-center px-4 py-2 text-green-700 rounded-lg border border-green-700 hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleExport}
              disabled={exportLoading}
            >
              {exportLoading ? <Spin size="small" className="mr-2" /> : <FaDownload className="mr-2" />} Export
            </button>
          </div>
          
          <div className="mb-5">
            <div className="flex gap-4 items-center mb-4">
              <div className="relative w-full">
                <FaSearch className="absolute left-3 top-1/2 text-gray-400 transform -translate-y-1/2" />
                <input
                  className="py-2 pr-4 pl-10 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-800 focus:border-transparent"
                  placeholder="Search assets..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            
            {showFilters && (
              <div className="p-5 mb-5 bg-gray-50 rounded-xl border border-gray-200 shadow-sm">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="flex items-center text-sm font-semibold text-gray-600">
                    <FaFilter className="mr-2" /> Advanced Filters
                  </h3>
                  
                  <div className="flex gap-3">
                    <button 
                      onClick={clearFilters}
                      className="px-3 py-1.5 rounded-md border border-gray-300 text-gray-600 text-sm hover:bg-gray-100"
                    >
                      Clear All
                    </button>
                    <button 
                      onClick={() => setShowFilters(false)}
                      className="px-3 py-1.5 rounded-md bg-blue-800 text-white text-sm hover:bg-blue-900"
                    >
                      Apply Filters
                    </button>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 gap-4 mb-4 md:grid-cols-3">
                  <div>
                    <div className="flex items-center mb-2">
                      <FaList className="mr-2 text-blue-800" size={14} />
                      <span className="text-sm font-medium text-gray-700">
                        Asset Name
                      </span>
                    </div>
                    <Input 
                      placeholder="Filter by asset name" 
                      value={assetNameFilter}
                      onChange={(e) => setAssetNameFilter(e.target.value)}
                      className="w-full rounded-lg"
                    />
                  </div>
                  
                  <div>
                    <div className="flex items-center mb-2">
                      <FaTag className="mr-2 text-blue-800" size={14} />
                      <span className="text-sm font-medium text-gray-700">
                        Stock Status
                      </span>
                    </div>
                    <Select
                      allowClear
                      placeholder="Filter by stock status"
                      className="w-full rounded-lg"
                      onChange={(value) => setClosingStockFilter(value)}
                      value={closingStockFilter}
                      options={[
                        { value: 'zero', label: 'Out of Stock (0)' },
                        { value: 'low', label: 'Low Stock (1-5)' },
                        { value: 'normal', label: 'Normal Stock (>5)' }
                      ]}
                    />
                  </div>
                  
                  <div>
                    <div className="flex items-center mb-2">
                      <FaCalendarAlt className="mr-2 text-blue-800" size={14} />
                      <span className="text-sm font-medium text-gray-700">
                        Stock Range
                      </span>
                    </div>
                    <Input.Group compact>
                      <Input 
                        style={{ width: '45%' }} 
                        placeholder="Min" 
                        type="number"
                        min={0}
                        value={stockRangeFilter[0]}
                        onChange={(e) => setStockRangeFilter([e.target.value, stockRangeFilter[1]])}
                      />
                      <Input 
                        style={{ width: '45%', marginLeft: '10%' }} 
                        placeholder="Max" 
                        type="number"
                        min={0}
                        value={stockRangeFilter[1]}
                        onChange={(e) => setStockRangeFilter([stockRangeFilter[0], e.target.value])}
                      />
                    </Input.Group>
                  </div>
                </div>
              </div>
            )}
            
            {filteredData.length > 0 && (
              <div className="mb-4">
                <div className="flex justify-between items-center">
                  <p className="text-sm text-gray-600">
                    Showing <span className="font-semibold">{paginatedData.length}</span> of <span className="font-semibold">{filteredData.length}</span> records
                    {(assetNameFilter || closingStockFilter || (stockRangeFilter[0] && stockRangeFilter[1])) && (
                      <span className="font-medium text-blue-800"> (filtered)</span>
                    )}
                  </p>
                  {filteredData.length > 0 && (
                    <p className="text-sm text-gray-600">
                      Page {page + 1} of {Math.ceil(filteredData.length / rowsPerPage)}
                    </p>
                  )}
                </div>
                
                {(assetNameFilter || closingStockFilter || (stockRangeFilter[0] && stockRangeFilter[1])) && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {assetNameFilter && (
                      <span className="inline-flex items-center px-3 py-1 text-xs font-medium text-blue-800 bg-blue-100 rounded-full">
                        Asset: {assetNameFilter}
                        <button 
                          onClick={() => setAssetNameFilter('')} 
                          className="ml-1.5 text-blue-800 hover:text-blue-900"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </span>
                    )}
                    {closingStockFilter && (
                      <span className="inline-flex items-center px-3 py-1 text-xs font-medium text-blue-800 bg-blue-100 rounded-full">
                        Status: {closingStockFilter === 'zero' ? 'Out of Stock' : closingStockFilter === 'low' ? 'Low Stock' : 'Normal Stock'}
                        <button 
                          onClick={() => setClosingStockFilter(null)} 
                          className="ml-1.5 text-blue-800 hover:text-blue-900"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </span>
                    )}
                    {stockRangeFilter[0] && stockRangeFilter[1] && (
                      <span className="inline-flex items-center px-3 py-1 text-xs font-medium text-blue-800 bg-blue-100 rounded-full">
                        Stock: {stockRangeFilter[0]} - {stockRangeFilter[1]}
                        <button 
                          onClick={() => setStockRangeFilter([null, null])} 
                          className="ml-1.5 text-blue-800 hover:text-blue-900"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="overflow-hidden bg-white rounded-xl shadow-md">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-4 py-4 text-sm font-semibold text-left text-gray-600">Action</th>
                <th scope="col" className="px-4 py-4 text-sm font-semibold text-left text-gray-600">Asset Code / Name</th>
                {!isMobile && (
                  <>
                    <th scope="col" className="px-4 py-4 text-sm font-semibold text-center text-gray-600">Opening Stock</th>
                    <th scope="col" className="px-4 py-4 text-sm font-semibold text-center text-gray-600">Inward QTY</th>
                    <th scope="col" className="px-4 py-4 text-sm font-semibold text-center text-gray-600">Outward QTY</th>
                  </>
                )}
                <th scope="col" className="px-4 py-4 text-sm font-semibold text-center text-gray-600">Closing Stock</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedData.length > 0 ? (
                paginatedData.map((row) => (
                  <tr 
                    key={row.id}
                    className="transition-colors duration-150 hover:bg-blue-50"
                  >
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex space-x-2">
                        <button
                          className="p-1.5 bg-blue-50 rounded-md text-blue-600 hover:bg-blue-100 transition-colors"
                          onClick={() => navigate("/assets/inventory/issue", { state: row })}
                          title="Issue Asset"
                        >
                          <FaBoxOpen size={14} />
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div>
                        <span className="font-medium text-gray-800">{row.asset_name}</span>
                        <div className="text-xs text-gray-500">{row.item_code}</div>
                      </div>
                    </td>
                    {!isMobile && (
                      <>
                        <td className="px-4 py-3 font-medium text-center whitespace-nowrap">{row.opening_stock}</td>
                        <td className="px-4 py-3 font-medium text-center text-green-600 whitespace-nowrap">{row.inward_qty}</td>
                        <td className="px-4 py-3 font-medium text-center text-red-600 whitespace-nowrap">{row.outward_qty}</td>
                      </>
                    )}
                    <td className="px-4 py-3 text-center whitespace-nowrap">
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        parseInt(row.closing_stock) === 0 ? 'bg-red-50 text-red-700' : 
                        parseInt(row.closing_stock) <= 5 ? 'bg-amber-50 text-amber-700' : 
                        'bg-green-50 text-green-700'
                      }`}>
                        {row.closing_stock}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={isMobile ? 3 : 6} className="px-4 py-6 text-center">
                    <p className="font-medium text-gray-600">
                      No records found
                    </p>
                    {(assetNameFilter || closingStockFilter || (stockRangeFilter[0] && stockRangeFilter[1]) || searchTerm) && (
                      <p className="mt-1 text-sm text-gray-500">
                        Try adjusting your filters
                      </p>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination controls */}
      <div className="flex justify-between items-center mt-6 mb-2">
        <div className="flex gap-3 items-center">
          <span className="text-sm text-gray-600">
            Rows per page:
          </span>
          <select
            value={rowsPerPage}
            onChange={(e) => setRowsPerPage(Number(e.target.value))}
            className="px-2 py-1 text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-800 focus:border-transparent"
          >
            {[5, 10, 25, 50].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                {pageSize}
              </option>
            ))}
          </select>
        </div>
        
        <div className="flex gap-2">
          <button
            disabled={page === 0}
            onClick={() => setPage((prev) => prev - 1)}
            className={`px-4 py-2 rounded-lg border text-sm min-w-[100px] ${page === 0 
              ? 'border-gray-200 text-gray-400 cursor-not-allowed' 
              : 'border-blue-800 text-blue-800 hover:bg-blue-50'}`}
          >
            Previous
          </button>
          <button
            disabled={paginatedData.length < rowsPerPage || (page + 1) * rowsPerPage >= filteredData.length}
            onClick={() => setPage((prev) => prev + 1)}
            className={`px-4 py-2 rounded-lg border text-sm min-w-[100px] ${paginatedData.length < rowsPerPage || (page + 1) * rowsPerPage >= filteredData.length 
              ? 'border-gray-200 text-gray-400 cursor-not-allowed' 
              : 'border-blue-800 text-blue-800 hover:bg-blue-50'}`}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssetInventoryChild;
